import { Box, Collapse, Icon, IconButton, styled } from '@mui/material';
import { useState } from 'react';
import useAnalytics from '@/hooks/use-analytics';

interface ExpandableContainerProps {
  summary: React.ReactNode;
  details: React.ReactNode;
}

interface StyledButtonProps {
  open: boolean;
}

export const ExpandableContainer: React.FC<ExpandableContainerProps> = ({ summary, details }) => {
  const [open, setOpen] = useState(false);
  const { trackEvent } = useAnalytics();

  const handleToggle = () => {
    if (open) {
      trackEvent(`EXPANDABLE_CONTAINER_COLLAPSED`);
    } else {
      trackEvent(`EXPANDABLE_CONTAINER_EXPANDED`);
    }
    setOpen((prev) => !prev);
  };

  return (
    <>
      <StyledBox>
        <span>{summary}</span>

        {/* Only this button controls expansion */}
        <StyledIconButton
          onClick={handleToggle}
          aria-expanded={open}
          aria-label={open ? 'Collapse' : 'Expand'}
          open={open}
        >
          <Icon>expand_more</Icon>
        </StyledIconButton>
      </StyledBox>
      <StyledCollapse
        in={open}
        timeout='auto'
        unmountOnExit
      >
        {details}
      </StyledCollapse>
    </>
  );
};

const StyledIconButton = styled(IconButton)<StyledButtonProps>`
  color: ${({ theme }) => theme.palette.primary.main};
  outline: 1px solid ${({ theme }) => theme.palette.primary.main};
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  margin-left: ${({ theme }) => theme.spacing(4)};
`;

const StyledBox = styled(Box)`
  display: flex;
  padding: ${({ theme }) => theme.spacing(4)};
  align-items: center;
  justify-content: space-between;
`;

const StyledCollapse = styled(Collapse)`
  border-top: 1px solid ${({ theme }) => theme.palette.border.secondary};
  padding: ${({ theme }) => theme.spacing(4)} 0;
  margin: 0 ${({ theme }) => theme.spacing(4)};
`;
